import { isStartupChecklistTemplate } from "../../startupChecklists/startupChecklistUtils";
import { Divider, Paper, Stack, Tabs, Text, Title } from "@mantine/core";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect } from "react";
import GlobalFileUploaderTable from "../../../../components/FileUploader/GlobalFileUploaderTable";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useGetFilesFromAzureBlobStorageQuery } from "../../../azureBlobStorageRTK";
import ReportIdUrlSync from "../ReportIdInSync";
import { useGetSingleReportBoardQuery } from "../reportRTK";
import { selectHttpSingleReportBoard } from "../reportSelectors";
import { resetEditedDataInRedux } from "../reportSlice";
import SettingsReport from "./SettingsReport";
import SingleReport from "./SingleReport";

const MainReportPage: React.FC<{ reportId: string }> = ({ reportId }) => {
  const [activeTab, setActiveTab] = React.useState<"REPORT" | "SETTINGS" | "FILES">("REPORT");

  const project = useAppSelector((state) => state.adminReducer.project);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(resetEditedDataInRedux());
  }, [dispatch, reportId]); // <-- reset när man byter report

  const { data: activeReport } = useGetSingleReportBoardQuery(
    {
      projectId: project._id,
      reportBoardId: reportId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const handleTabChange = (tab: "REPORT" | "SETTINGS" | "FILES") => {
    setActiveTab(tab);
  };

  return (
    <Stack gap={0} w="100%" p="sm" style={{ overflow: "hidden", height: "100%", background: "white" }}>
      <ReportIdUrlSync />
      {activeReport != null &&
        (isStartupChecklistTemplate(activeReport.template) ? (
          <SingleReport key={`${project._id}/${reportId}`} />
        ) : (
          <Tabs
            value={activeTab}
            onChange={(value) => handleTabChange(value as typeof activeTab)}
            style={{ background: "white", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
          >
            <Tabs.List style={{ background: "white", margin: 0, flexShrink: 0 }}>
              <Tabs.Tab value="REPORT">Rapport</Tabs.Tab>
              <Tabs.Tab value="FILES">Filer</Tabs.Tab>
              <Tabs.Tab value="SETTINGS">Innstillinger</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="REPORT" pt={0} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <SingleReport key={`${project._id}/${reportId}`} />
            </Tabs.Panel>
            <Tabs.Panel value="FILES" pt="md" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Files projectId={project._id} />
            </Tabs.Panel>
            <Tabs.Panel value="SETTINGS" pt="md" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <SettingsReport reportBoard={activeReport} />
            </Tabs.Panel>
          </Tabs>
        ))}
    </Stack>
  );
};

export default MainReportPage;

const Files: React.FC<{ projectId: string }> = ({ projectId }) => {
  const activeReport = useAppSelector(selectHttpSingleReportBoard);
  const path = `${projectId}/report/${activeReport?._id}/images`;

  const {
    data: files,
    isLoading: loadingFiles,
    isError: errorFetchingFiles,
  } = useGetFilesFromAzureBlobStorageQuery({ projectId, path }, { skip: activeReport == null });

  const columns: ColDef[] = [
    {
      field: "docxImageTag",
      headerName: "Docx tag",
      cellRenderer: (params) => {
        const blobName = params.data?.blobName || "";
        const parts = blobName.split("/");
        const docxImageTag = parts.length >= 2 ? parts[parts.length - 2] : "";
        return <b>%{docxImageTag}</b>;
      },
    },
    { field: "fileName", headerName: "Filnavn", flex: 1 },
    { field: "height", headerName: "Høyde" },
    { field: "width", headerName: "Bredde" },
  ];

  if (activeReport == null) {
    return null;
  }

  return (
    <Stack gap="md">
      <GlobalFileUploaderTable path={`${activeReport.project}/report/${activeReport._id}/files`} />
      <Divider />
      <div>
        <Title order={4}>Bilder</Title>
        <Text size="sm" c="dimmed">
          Bilder knyttet til docx-tagger i rapporten.
        </Text>
      </div>
      <Paper withBorder radius="md" p="xs">
        <div className="ag-theme-alpine" style={{ width: "100%" }}>
          <AgGridReact
            domLayout="autoHeight"
            rowData={files}
            columnDefs={columns}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
            pagination={true}
            paginationPageSize={10}
          />
        </div>
      </Paper>
    </Stack>
  );
};
